import React from 'react';
import { Table } from 'antd';
import { ROUTER_COOKIES } from 'app/AppRouter';
import { Container, Flex, Li, Link, MarginBox, Paragraph, Section, SectionHeader, Text, Ul } from 'UI';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { Country } from 'utils/i18n/Country';
import { Language } from 'utils/i18n/Language';

export const PersonalDataBE = () => {
  const FR_OBJECTIVE_TABLE_HEADER = 'Objectifs';
  const FR_LEGAL_TABLE_HEADER = 'Base Juridique';
  const NL_OBJECTIVE_TABLE_HEADER = 'Doelstellingen';
  const NL_LEGAL_TABLE_HEADER = 'Rechtsgrond';
  useResetScroll();

  return (
    <Container size={'md'} alignText={'justify'}>
      <Flex direction={'column'}>
        <MarginBox mt={15} />
        <Flex direction={'column'} align={'center'}>
          <Text type={'h5_bold'}>DÉCLARATION DE CONFIDENTIALITÉ</Text>
          <Text type={'h5_bold'}>POUR RPARTSTORE</Text>
          {Language.frenchBelgium.name}
          <MarginBox mt={30} />
        </Flex>
        <Section>
          <Paragraph>
            Le Groupe Renault, dont Renault Belgium S.A. fait partie, veille en permanence au respect de la
            réglementation relative à la protection des données à caractère personnel (ci-après dénommées « Données
            Personnelles »), telle que décrite dans notre Politique de Protection des Données Personnelles disponible à
            l&apos;adresse{' '}
            <Link to={'https://www.renault.be/bescherming-persoonsgegevens.html'} external newTab>
              https://www.renault.be/bescherming-persoonsgegevens.html
            </Link>
            .
          </Paragraph>
          <Paragraph>
            Renault propose le site{' '}
            <Text type="base" bold>
              RpartStore
            </Text>{' '}
            <Link to={'https://rpartstore.renault.com'} external newTab>
              https://rpartstore.renault.com
            </Link>{' '}
            (la « Plateforme ») pour (i) faciliter la consultation de la documentation technique, proposer à la vente
            des pièces détachées et des accessoires pour véhicules (les « Produits »), (ii) permettre aux utilisateurs
            de signaler des incidents techniques sur la Plateforme et (iii) générer une mise en relation commerciale
            directe entre vendeurs (professionnels du réseau Renault) et acheteurs (pour un usage professionnel
            uniquement). Dans le cadre de la visite et de l&apos;interaction avec la Plateforme, Renault, en tant que
            responsable du traitement, collecte des données à caractère personnel directement auprès des utilisateurs de
            la Plateforme (« Utilisateurs »), ainsi que, le cas échéant, indirectement, des données à caractère
            personnel relatives aux clients finaux des Utilisateurs (« Tiers concernés »). Conformément aux Conditions
            Générales d&apos;Utilisation de la Plateforme (ci-après dénommées les « Conditions Générales »), Renault
            entend assurer la plus grande transparence sur le traitement des données à caractère personnel à travers la
            plateforme. La présente déclaration de confidentialité a pour objet d&apos;informer les Utilisateurs des
            conditions dans lesquelles Renault traite les données à caractère personnel les concernant, ou concernant
            leurs représentants respectifs qui sont des personnes physiques (« vous ») et, le cas échéant, les données à
            caractère personnel de Tiers concernés, ainsi que des droits et options dont disposent les Utilisateurs pour
            contrôler les données à caractère personnel et protéger la vie privée. La présente déclaration de
            confidentialité ne s&apos;applique qu&apos;au traitement lié à la Plateforme.
          </Paragraph>
          <Paragraph>
            Les termes en majuscules utilisés mais non définis dans le présent avis de confidentialité ont la
            signification qui leur est donnée dans les conditions générales. Dans le présent document, « nous », « notre
            », « nos » ou « Renault » font référence à Renault Belgique.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'1. QUI TRAITE VOS DONNÉES PERSONNELLES ?'} />
          <Paragraph>
            Renault Belgium S.A., située Chaussée de Mons 281, 1070 Anderlecht, traite les données personnelles aux fins
            énumérées ci-dessous (voir 3. À quoi servent les données personnelles ?) en tant que responsable du
            traitement.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'2. QUELLES DONNÉES PERSONNELLES TRAITONS-NOUS ?'} />
          <Paragraph>
            Les « données à caractère personnel » sont toutes les données qui se rapportent à une personne physique et
            qui peuvent être utilisées pour l&apos;identifier directement (comme votre prénom et/ou votre nom) ou
            indirectement (par exemple, la combinaison d&apos;un code postal et d&apos;un numéro de maison). Si
            l&apos;Utilisateur lui-même n&apos;est pas une personne physique, les Données Personnelles sont les données
            relatives à ses représentants qui sont des personnes physiques.
          </Paragraph>
          <Paragraph>
            De manière générale, nous nous engageons à ne collecter que les Données Personnelles nécessaires à chacune
            des finalités pour lesquelles nous avons besoin de Données Personnelles.
          </Paragraph>
          <Paragraph>
            Les données personnelles que nous collectons dépendent de notre interaction avec l&apos;utilisateur et
            peuvent inclure des informations sur :
          </Paragraph>
          <Ul>
            <Li>
              <Text type={'text'}>votre connexion/vérification sur la Plateforme ; </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                vos données d&apos;identification et vos coordonnées professionnelles (email professionnel, nom, prénom)
                ;
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                votre utilisation de la Plateforme et l&apos;appareil que vous utilisez pour visiter la Plateforme, y
                compris les fichiers journaux (relatifs au nombre de visites, à la page visitée, au navigateur web
                utilisé), l&apos;adresse IP, le fuseau horaire et les cookies (pour plus d&apos;informations,
                veuillez-vous référer à la page « Cookies » de la Plateforme) ;
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                les devis que vous avez effectués via la Plateforme concernant le diagnostic, la réparation ou le
                recyclage (« Opérations techniques ») du véhicule du Tiers impliqué, y compris les devis qui ne donnent
                pas lieu à la soumission d&apos;une Opération technique ;
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                une commande de Produits auprès du réseau de distribution Renault R1 (les données collectées sont
                notamment les coordonnées professionnelles du contact commercial qui recevra la livraison) ;
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                la relation commerciale (c&apos;est-à-dire vos éventuelles demandes d&apos;assistance concernant le
                fonctionnement de la Plateforme, vos réclamations et/ou l&apos;exercice de vos droits en matière de
                données personnelles).
              </Text>
            </Li>
          </Ul>
          <MarginBox mt={15} />
          <Paragraph>
            Les informations nécessaires pour répondre à une demande ou pour remplir un contrat ou une obligation légale
            sont mentionnées dans les formulaires de collecte. Si vous ne souhaitez pas fournir les données
            obligatoires, nous pourrions ne pas être en mesure de traiter votre demande ou de vous fournir les services
            correspondants.
          </Paragraph>
          <Paragraph>
            Conformément à nos Conditions générales, la Plateforme n&apos;est pas destinée aux mineurs et nous ne
            collectons donc pas sciemment de données les concernant. Les produits et services que nous proposons
            s&apos;adressent principalement aux adultes. Par conséquent, nous n&apos;effectuons pas de traitement
            spécifique sur les mineurs.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'3. À QUOI SERVENT LES DONNÉES PERSONNELLES ?'} />
          <Paragraph>
            Nous expliquons le contexte de la collecte des données à caractère personnel, les finalités pour lesquelles
            nous les traitons et la durée de leur conservation.
          </Paragraph>
          <MarginBox mt={15} />
          <Section>
            <SectionHeader title={'3.1 CONTEXTE DE LA COLLECTE '} />
            <Paragraph>Nous collectons des Données Personnelles notamment lorsque :</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>vous utilisez la Plateforme</Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  vous interagissez directement avec Renault via la Plateforme (remplir un formulaire, nous contacter)
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  vous visitez la Plateforme, qui peut utiliser des cookies ou d&apos;autres traceurs
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  vous effectuez une demande de support technique concernant le fonctionnement de la Plateforme,
                  enregistrez une Opération Technique ou éditez et envoyez une opération technique ou, le cas échéant,
                  établissez une Demande de Service.
                </Text>
              </Li>
            </Ul>
          </Section>
          <Section>
            <SectionHeader title={'3.2. FINALITES DU TRAITEMENT DES DONNEES A CARACTERE PERSONNEL'} />
            <Paragraph>
              Dans le cadre de notre relation et conformément au contexte dans lequel vos Données Personnelles ont été
              collectées, nous pouvons utiliser vos Données Personnelles pour :
            </Paragraph>
            <Ul type={'none'}>
              <Li>
                <Text type={'text'}>A – Gérer nos interactions</Text>
              </Li>
              <Li>
                <Text type={'text'}>B – Gérer notre relation commerciale</Text>
              </Li>
              <Li>
                <Text type={'text'}>C – Réaliser des études et des analyses</Text>
              </Li>
              <Li>
                <Text type={'text'}>D – Gérer nos activités de marketing</Text>
              </Li>
            </Ul>
            <MarginBox mt={10} />
            <Section>
              <SectionHeader title={'3.2.A. GÉRER NOS INTERACTIONS'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: FR_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-a',
                    width: '50%',
                  },
                  { title: FR_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-a', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    objectives: 'Mesurer la fréquentation de la Plateforme et gérer sa maintenance et sa sécurité',
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est basé sur votre consentement à placer/lire des cookies « non essentiels » dans votre terminal (voir la page « Cookies » de la Plateforme), ou sur notre intérêt légitime (vous fournir une Plateforme sécurisée)`,
                  },
                  {
                    id: '1',
                    objectives: 'Gestion de votre authentification sur la Plateforme',
                    legal:
                      // eslint-disable-next-line max-len
                      "Ce traitement est justifié par l'exécution du contrat conclu par l'Utilisateur avec nous (régi par les Conditions Générales)",
                  },
                  {
                    id: '2',
                    objectives: "Permettre à l'Utilisateur de se connecter à la Plateforme",
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que l'Utilisateur a conclu avec nous (régi par les Conditions Générales)`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={'3.2.B. GÉRER NOS RELATIONS COMMERCIALES'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: FR_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-b',
                    width: '50%',
                  },
                  { title: FR_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-b', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    // eslint-disable-next-line max-len
                    objectives: `Pour permettre à l'Utilisateur d'effectuer des Opérations Techniques, notamment dans le cadre de la réparation des véhicules`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que nous avons conclu avec l'Utilisateur (régi par les Conditions Générales)`,
                  },
                  {
                    id: '1',
                    // eslint-disable-next-line max-len
                    objectives: `Permettre à l'Utilisateur de consulter la documentation technique après-vente relative aux produits Renault (tels que : pièces détachées, temps de main d'œuvre, méthodes de réparation et de diagnostic, tutoriels, sans être exhaustif) et/ou aux marques d'autres constructeurs automobiles`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que nous avons conclu avec l'Utilisateur (régi par les Conditions Générales)`,
                  },
                  {
                    id: '2',
                    // eslint-disable-next-line max-len
                    objectives: `Pour permettre à l'Utilisateur de payer le montant lié aux Opérations Techniques et, le cas échéant, d'effectuer une Demande de Service via la Plateforme`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que nous avons conclu avec l'Utilisateur (régi par les Conditions Générales)`,
                  },
                  {
                    id: '3',
                    objectives: `Gestion de notre relation commerciale`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que nous avons conclu avec les utilisateurs (régi par les conditions générales)`,
                  },
                  {
                    id: '4',
                    objectives: `Gérer les demandes de service relatives au fonctionnement de la Plateforme`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est justifié par l'exécution du contrat que nous avons conclu avec les utilisateurs (régi par les conditions générales)`,
                  },
                  {
                    id: '5',
                    // eslint-disable-next-line max-len
                    objectives: `Répondre à toute demande d'exercice des droits relatifs à vos données à caractère personnel concernant un (représentant d'un) utilisateur (voir la section 5 ci-dessous sur « vos droits »)`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est basé sur nos obligations légales et peut impliquer la vérification de l'identité du demandeur`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={"3.2.C. RÉALISATION D'ÉTUDES ET D'ANALYSES"} />
              <Paragraph>
                Des études et des analyses sont réalisées pour mesurer nos performances, évaluer la qualité de nos
                produits et services et le niveau de satisfaction de nos clients et les améliorer en permanence.
              </Paragraph>
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: FR_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-c',
                    width: '50%',
                  },
                  { title: FR_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-c', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    // eslint-disable-next-line max-len
                    objectives: `Gestion de nos enquêtes de satisfaction, y compris la réponse (c'est-à-dire votre opinion), Mesurer la satisfaction des utilisateurs à l'égard de la plateforme`,
                    legal: `Ce traitement est basé sur nos intérêts légitimes (mesurer la performance de notre activité)`,
                  },
                  {
                    id: '1',
                    objectives: `Contrôler notre activité (reporting)`,
                    // eslint-disable-next-line max-len
                    legal: `Ce traitement est basé sur notre intérêt légitime (améliorer nos produits et services)`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={'3.2.D. GÉRER NOS ACTIVITÉS DE MARKETING'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: FR_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-d',
                    width: '50%',
                  },
                  { title: FR_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-d', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    objectives: `Envoyer des campagnes publicitaires au réseau Réseau Renault (B2B) / Utilisateurs`,
                    legal: `Ce traitement est basé sur notre intérêt légitime (faire connaître nos produits)`,
                  },
                ]}
              />
              <MarginBox mt={15} />
              <Paragraph>
                Si nous devons traiter des données personnelles à des fins autres que celles énumérées dans le tableau
                ci-dessus, vous en serez informé au préalable et nous prendrons toutes les mesures supplémentaires qui
                pourraient être nécessaires pour garantir que tout traitement est conforme à la loi.
              </Paragraph>
            </Section>
          </Section>
          <Section>
            <SectionHeader title={'3.3. DURÉE DE CONSERVATION DES DONNÉES À CARACTÈRE PERSONNEL'} />
            <Paragraph>
              Conformément aux lois et réglementations applicables, nous nous engageons à conserver les Données
              Personnelles traitées dans le cadre de la Plateforme uniquement pendant la durée nécessaire pour atteindre
              l&apos;objectif visé, pour répondre à vos besoins ou pour respecter nos obligations légales.
            </Paragraph>
            <Paragraph>Pour déterminer cette durée, nous prenons notamment en compte les éléments suivants :</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>Le temps nécessaire au traitement d&apos;une demande ou d&apos;une plainte</Text>
              </Li>
              <Li>
                <Text type={'text'}>l&apos;intérêt que vous portez à notre plateforme </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  La nécessité de conserver un certain historique des interactions avec la Plateforme, cette durée
                  variant notamment selon que vous avez signé une Demande de Service ou non ;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>Nos obligations légales ou réglementaires.</Text>
              </Li>
            </Ul>
            <Paragraph>En particulier :</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>
                  Les données personnelles collectées via les cookies dans le cadre de l&apos;analyse de la performance
                  des contenus que vous consultez sur nos sites et de l&apos;affichage de publicités ciblées en ligne.
                  Les traceurs/cookies non essentiels, tels que les cookies publicitaires, seront conservés pour une
                  durée maximale de 13 mois à compter de la date de votre consentement (sous réserve que vous ne
                  retiriez pas votre consentement avant cette date). Les données à caractère personnel collectées par le
                  biais des cookies seront traitées pendant 25 mois, puis rendues anonymes. La durée de conservation de
                  vos choix (consentement ou refus des cookies dits non essentiels soumis au recueil préalable du
                  consentement) est de 6 mois à compter de la date de recueil de votre consentement. Pour plus
                  d&apos;informations, veuillez consulter notre page « Cookie » ;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  Les Données Personnelles traitées dans le cadre de la maintenance et de la sécurité de la Plateforme
                  et de nos bases de données sont conservées pendant 6 mois (ex : logs) ;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  Les Données Personnelles traitées dans le cadre de l&apos;historique de vos Opérations Techniques via
                  la Plateforme et/ou d&apos;une Demande de Service que vous avez envoyée sont conservées pendant 6
                  mois. Les devis effectués par l&apos;Utilisateur via la Plateforme sont conservés pendant 3 mois.
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  Les données à caractère personnel relatives aux demandes d&apos;exercice des droits d&apos;une
                  Personne concernée seront conservées pendant une durée maximale de 3 ans à compter de la conclusion de
                  votre demande, en fonction du droit exercé. Lorsque la collecte d&apos;une preuve d&apos;identité a
                  été nécessaire, ces données seront supprimées dès que la vérification aura été effectuée.
                </Text>
              </Li>
            </Ul>
            <Paragraph>
              Lorsque nous n&apos;avons plus besoin de vos données personnelles, elles sont supprimées de nos systèmes
              ou anonymisées afin que nous ne puissions plus vous identifier.
            </Paragraph>
            <Paragraph>
              Toutefois, il peut être nécessaire d&apos;archiver certaines données à caractère personnel afin de
              répondre à toute procédure judiciaire, pendant le délai de prescription prévu par la loi applicable.
            </Paragraph>
          </Section>
        </Section>
        <Section>
          <SectionHeader title={'4. QUI A ACCÈS À VOS DONNÉES PERSONNELLES ?'} />
          <Paragraph>
            Pour atteindre les objectifs définis dans le présent avis de confidentialité et uniquement dans la mesure
            nécessaire à la réalisation de ces objectifs :
          </Paragraph>
          <Ul>
            <Li>
              nous veillons à ce que seules les personnes dûment autorisées à traiter les données à caractère personnel
              conformément à leurs fonctions et devoirs puissent le faire.
            </Li>
            <Li>
              nous pouvons transférer certaines données à caractère personnel aux destinataires suivants :
              <Ul type={'circle'}>
                <MarginBox mt={10} />
                <Li>
                  aux membres du réseau Renault (c&apos;est-à-dire les membres du réseau R1 auprès desquels
                  l&apos;Utilisateur est autorisé à passer une commande de Produits), sous réserve de votre consentement
                  si la loi l&apos;exige.
                </Li>
                <MarginBox mt={10} />
                <Li>
                  En effet, Renault et les membres de son réseau ont conclu une convention par laquelle ils
                  s&apos;engagent, en tant que responsables de traitement indépendants, à respecter les obligations qui
                  leur incombent en vertu des lois et règlements relatifs à la protection des données à caractère
                  personnel, et notamment les principes suivants :{' '}
                  <Ul>
                    <MarginBox mt={7} />
                    <Li>traiter les données à caractère personnel de manière licite et loyale ;</Li>
                    <Li>garantir l&apos;exactitude et la qualité des données ;</Li>
                    <Li>
                      mettre en œuvre toutes les mesures techniques et organisationnelles appropriées pour assurer un
                      niveau de sécurité proportionnel au risque et coopérer en cas de violation des données ;
                    </Li>
                    <Li>à n&apos;engager que des sous-traitants fiables ;</Li>
                    <Li>à mettre en œuvre des garanties appropriées pour le transfert des données ;</Li>
                    <Li>
                      respecter les droits des personnes concernées, y compris le droit d&apos;être informé sur le
                      traitement des données.
                    </Li>
                  </Ul>
                  <MarginBox mt={10} />
                  En ce qui concerne le dernier principe, les employés des utilisateurs du Réseau Renault sont informés
                  que la présente note d&apos;information sur la protection de la vie privée complète les informations
                  sur le traitement des données qui peuvent vous avoir été fournies par votre employeur en tant que
                  membre professionnel du Réseau Renault.
                </Li>
              </Ul>
            </Li>
            <MarginBox mt={10} />
            <Li>
              des sous-traitants de données personnelles au sens de la réglementation applicable en matière de données
              personnelles, pour l&apos;exécution des tâches énumérées ci-dessous. Nous assurons la sécurisation de ces
              relations (contrats, audits, garanties et tests de sécurité...) et les instruisons des tâches que nous
              leur confions notamment :
              <Ul type={'circle'}>
                <MarginBox mt={7} />
                <Li>Hébergement,</Li>
                <Li>
                  Sécurité et maintenance de la Plateforme et des bases de données collectées et/ou traitées via la
                  Plateforme,
                </Li>
                <Li>Prospection commerciale auprès des membres professionnels du réseau Renault (B2B),</Li>
                <Li>
                  la gestion des relations avec les clients et l&apos;assistance technique liée à l&apos;exploitation de
                  la plate-forme
                </Li>
                <Li>la mesure de l&apos;audience de la plate-forme</Li>
                <Li>Mesure de la satisfaction des utilisateurs de la Plateforme.</Li>
                <MarginBox mt={7} />
              </Ul>
            </Li>
          </Ul>
          <Paragraph>
            Nous pouvons être amenés à divulguer des Données Personnelles à des tiers pour nous conformer à une
            obligation légale ou à une décision administrative ou judiciaire.
          </Paragraph>
          <Paragraph>
            Renault peut partager les Données Personnelles avec des tiers lors du transfert des obligations et des
            droits relatifs à la relation contractuelle entre vous et Renault à ces tiers en cas de transfert d&apos;une
            unité commerciale, de fusion par création d&apos;une nouvelle société, de scission ou de changement de
            contrôle affectant Renault. Avant un tel événement, Renault vous informera séparément des informations
            relatives au partage de vos données et vous demandera votre consentement si la loi l&apos;exige.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'5. QUELS SONT VOS DROITS EN TANT QUE PERSONNE CONCERNÉE ?'} />
          <Section>
            <SectionHeader title={'5.1. VOS DROITS'} />
            <Paragraph>
              En tant que personne concernée, vous disposez de plusieurs droits en vertu du règlement sur la protection
              des données à caractère personnel :
            </Paragraph>
            <Paragraph>
              Le droit de vous opposer au traitement de vos données personnelles, à condition que vous ayez des raisons
              liées à votre situation particulière, et le droit de demander la limitation du traitement de vos données
              personnelles, dans certains cas comme stipulé dans les règlements.
            </Paragraph>
            <Paragraph>
              Le droit de vous opposer à nos communications : vous pouvez demander à ne plus recevoir de communications
              de notre part à tout moment.
            </Paragraph>
            <Paragraph>
              Le droit de retirer votre consentement à tout moment pour les finalités pour lesquelles nous avons obtenu
              votre consentement (voir ci-dessus 3.2. La finalité du traitement de vos données à caractère personnel).
            </Paragraph>
            <Paragraph>
              Droit à l&apos;information : vous avez le droit de recevoir des informations claires, transparentes et
              compréhensibles sur la manière dont nous utilisons vos données à caractère personnel et sur vos droits. Le
              présent avis de confidentialité en est un exemple.
            </Paragraph>
            <Paragraph>
              Droit d&apos;accès à vos données à caractère personnel : vous avez le droit d&apos;obtenir des
              informations sur le traitement de vos données à caractère personnel (notamment quelles données sont
              utilisées, à quelles fins, etc.)
            </Paragraph>
            <Paragraph>
              Droit de rectification : vous avez le droit de corriger vos données à caractère personnel si elles sont
              inexactes ou incomplètes, malgré nos efforts pour les mettre à jour, afin que nous puissions remplir notre
              obligation de disposer de données actualisées à votre sujet.
            </Paragraph>
            <Paragraph>
              Un droit à la portabilité de vos données, c&apos;est-à-dire, sous certaines conditions, le droit de
              recevoir les données à caractère personnel que vous nous avez fournies dans un format informatique
              structuré et couramment utilisé, et de les transférer à un tiers si cela est techniquement possible.
            </Paragraph>
            <Paragraph>
              Droit à l&apos;effacement (ou droit à l&apos;oubli) : vous avez le droit d&apos;obtenir l&apos;effacement
              ou la suppression de vos données à caractère personnel. Ce droit peut être limité au regard de nos
              obligations contractuelles ou légales (contrat en cours) (notamment la prévention d&apos;une action en
              justice).
            </Paragraph>
            <Paragraph>
              Enfin, vous avez le droit de déposer une plainte auprès d&apos;une autorité de contrôle pour la protection
              des données personnelles aux Pays-Bas, l&apos;Autorité des données personnelles. Nous vous encourageons à
              nous contacter avant de déposer une plainte afin que nous puissions essayer de résoudre votre problème
              ensemble.
            </Paragraph>
            <Paragraph>
              Droit de rectification : vous avez le droit de corriger vos données à caractère personnel si elles sont
              inexactes ou incomplètes, malgré nos efforts pour les mettre à jour, afin que nous puissions remplir notre
              obligation de disposer de données actualisées à votre sujet.
            </Paragraph>
          </Section>
          <Section>
            <SectionHeader title={'5.2. COMMENT EXERCER VOS DROITS EN TANT QUE PERSONNE CONCERNÉE ?'} />
            <Paragraph>
              Pour exercer vos droits, vous pouvez à tout moment adresser votre demande par courrier à l&apos;adresse
              suivante :
            </Paragraph>
            <Paragraph>
              Renault Belgium S.A., Service juridique - Délégué à la protection des données, Bergensesteenweg 281, 1620
              Drogenbos, Belgique.
            </Paragraph>
            <Paragraph>ou</Paragraph>
            <Paragraph>
              par courrier électronique à l&apos;adresse suivante :{' '}
              <Link to={'mailto:dataprotection-com@renault.com'} external newTab>
                dataprotection-com@renault.com
              </Link>
              .
            </Paragraph>
            <Paragraph>
              Dans les deux cas, pour faciliter le traitement de votre demande, veuillez indiquer que votre demande
              concerne la Plateforme « RpartStore ».
            </Paragraph>
            <Paragraph>
              Si nous ne sommes pas sûrs de votre identité, il se peut que nous vous demandions de prouver votre
              identité (c&apos;est-à-dire que la copie de votre identité sera traitée uniquement pour répondre à votre
              demande et sera supprimée immédiatement après vérification).
            </Paragraph>
            <Paragraph>
              Nous nous efforcerons de répondre à votre demande dans les meilleurs délais, mais au plus tard un mois
              après réception de votre demande. Conformément à la réglementation applicable, nous nous réservons le
              droit de porter ce délai à trois mois si votre demande est particulièrement complexe.
            </Paragraph>
          </Section>
        </Section>
        <Section>
          <SectionHeader title={'6. COMMENT VOS DONNÉES PERSONNELLES SONT-ELLES PROTÉGÉES ?'} />
          <Paragraph>
            Vos données personnelles sont stockées sur des serveurs sécurisés. Nous prenons des mesures de sécurité et
            de protection des données appropriées, conformes aux dernières technologies, et nous l&apos;exigeons de nos
            sous-traitants et partenaires.
          </Paragraph>
          <Paragraph>
            Lorsque le traitement des données personnelles implique le transfert de données, nous nous assurons que ce
            transfert s&apos;effectue dans des conditions de sécurité et de protection des données appropriées.
          </Paragraph>
          {/*TODO*/}
          <Paragraph>
            des conditions qui garantissent un niveau adéquat de protection, de sécurité et de confidentialité.
          </Paragraph>
          <Paragraph>
            Dans la mesure du possible, les données personnelles sont traitées dans l&apos;Espace économique européen
            (EEE). Cependant, comme certains de nos prestataires de services ou leurs sous-traitants sont basés dans des
            pays en dehors de l&apos;EEE, vos données personnelles peuvent être traitées dans ces pays. Dans certains de
            ces pays, des règles différentes de celles de l&apos;Union européenne peuvent s&apos;appliquer aux données à
            caractère personnel. Dans ce cas, nous (i) veillons particulièrement à ce que ce transfert soit effectué
            conformément à la réglementation applicable et (ii) mettons en place des garanties qui assurent un niveau de
            protection adéquat de votre vie privée et de vos droits fondamentaux (notamment en utilisant les Clauses
            Contractuelles Types de la Commission européenne). Sur simple demande, adressée à l&apos;adresse mentionnée
            au point 5 « Quels sont vos droits ? », nous pouvons vous fournir de plus amples informations sur ces
            transferts (en particulier les clauses contractuelles types de la Commission européenne).
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'7. AUTRES INFORMATIONS DONT VOUS POUVEZ AVOIR BESOIN'} />
          <Paragraph>
            Nous pouvons également collecter automatiquement des informations techniques vous concernant lorsque vous
            visitez l&apos;un de nos sites ou utilisez l&apos;une de nos applications mobiles, notamment votre adresse
            IP, vos paramètres de navigation, au moyen de traceurs (cookies).
          </Paragraph>
          <Paragraph>
            Pour plus d&apos;informations, nous vous invitons à consulter la page «{' '}
            <Link to={ROUTER_COOKIES(Country.BE.key)} newTab>
              Cookies
            </Link>{' '}
            » de la Plateforme.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'8. MODIFICATION DE LA PRESENTE DECLARATION DE CONFIDENTIALITE'} />
          <Paragraph>
            Nous pouvons modifier la présente déclaration de confidentialité de temps à autre. Nous vous en informerons
            et/ou demanderons votre consentement lorsque cela sera nécessaire ou requis. Nous vous encourageons donc à
            consulter la présente déclaration de confidentialité chaque fois que vous visitez notre Plateforme afin de
            prendre connaissance de la version la plus récente.
          </Paragraph>
        </Section>
        <Flex justify={'flex-end'}>
          <Text type={'text'}>Version 202412</Text>
        </Flex>
        <MarginBox mt={200} />
        <Flex direction={'column'} align={'center'}>
          <Text type={'h5_bold'}>PRIVACYVERKLARING</Text>
          <Text type={'h5_bold'}>VOOR RPART STORE</Text>
          {Language.dutchBelgium.name}
          <MarginBox mt={30} />
        </Flex>
        <Section>
          <Paragraph>
            De Renault Groep, waarvan Renault België N.V. onderdeel uitmaakt, ziet er voortdurend op toe dat de
            regelgeving met betrekking tot de bescherming van persoonsgegevens (hierna &quot;Persoonsgegevens&quot;
            genoemd) wordt nageleefd, zoals beschreven in ons beleid voor de bescherming van persoonsgegevens dat
            beschikbaar is op{' '}
            <Link to={'https://www.renault.be/bescherming-persoonsgegevens.html'} external newTab>
              https://www.renault.be/bescherming-persoonsgegevens.html
            </Link>
            .
          </Paragraph>
          <Paragraph>
            Renault biedt de website{' '}
            <Text type="base" bold>
              RpartStore
            </Text>{' '}
            aan, te bereiken via{' '}
            <Link to={'https://rpartstore.renault.com'} external newTab>
              https://rpartstore.renault.com
            </Link>{' '}
            (het &quot;Platform&quot;) om i) het inzien van technische documentatie te vergemakkelijken,
            reserveonderdelen en accessoires voor voertuigen (de &quot;Producten&quot;) te koop aan te bieden, ii) de
            gebruiker in staat te stellen technische incidenten op het Platform te melden en iii) rechtstreeks
            commercieel contact te genereren tussen verkopers (professionals uit het Renaultnetwerk) en kopers
            (uitsluitend voor zakelijk gebruik). In het kader van het bezoeken van, en de interactie met, het Platform
            verzamelt Renault, als verantwoordelijke voor de verwerking, persoonsgegevens rechtstreeks van de gebruikers
            van het Platform (&quot;Gebruikers&quot;), evenals, indien van toepassing, indirect, persoonsgegevens met
            betrekking tot de eindklanten van de Gebruikers (&quot;Derde Betrokkene&quot;). In overeenstemming met de
            Algemene Voorwaarden voor het gebruik van het Platform (hierna de &quot;Algemene Voorwaarden&quot; genoemd),
            wil Renault de grootst mogelijke transparantie garanderen met betrekking tot de verwerking van
            persoonsgegevens via het Platform. Het doel van deze privacyverklaring is om Gebruikers te informeren over
            de voorwaarden waaronder Renault persoonsgegevens verwerkt van hen, dan wel hun respectieve
            vertegenwoordigers die natuurlijke personen zijn (&quot;u&quot;) en, indien van toepassing, de
            persoonsgegevens van Derde Betrokkenen, evenals de rechten en opties die Gebruikers hebben om
            persoonsgegevens te controleren en privacy te beschermen. Deze privacyverklaring is uitsluitend van
            toepassing op verwerking in verband met het Platform.
          </Paragraph>
          <Paragraph>
            Termen met een hoofdletter die in deze privacyverklaring worden gebruikt maar niet gedefinieerd, hebben de
            betekenis die eraan wordt gegeven in de Algemene Voorwaarden. In dit document verwijst &quot;wij&quot;,
            &quot;ons&quot;, &quot;onze&quot; of &quot;Renault&quot; naar Renault België.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'1. WIE VERWERKT UW PERSOONSGEGEVENS?'} />
          <Paragraph>
            Renault België N.V., gevestigd in Drogenbos aan de Bergensesteenweg 281, 1070 te Anderlecht, verwerkt
            Persoonsgegevens voor de hieronder genoemde doeleinden (zie 3. Waarvoor worden Persoonsgegevens gebruikt?)
            als verantwoordelijke voor de verwerking.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'2. WELKE PERSOONSGEGEVENS VERWERKEN WIJ?'} />
          <Paragraph>
            &quot;Persoonsgegevens&quot; zijn alle gegevens die betrekking hebben op een natuurlijke persoon en kunnen
            worden gebruikt om direct (zoals uw voor- en/of achternaam) of indirect (bijvoorbeeld de combinatie van een
            postcode en huisnummer) te identificeren. Als de Gebruiker zelf geen natuurlijke persoon is, zijn
            Persoonsgegevens de gegevens die betrekking hebben op diens vertegenwoordigers die natuurlijke personen
            zijn.
          </Paragraph>
          <Paragraph>
            In het algemeen verbinden wij ons ertoe enkel de Persoonsgegevens te verzamelen die noodzakelijk zijn voor
            elk van de doeleinden waarvoor wij Persoonsgegevens nodig hebben.
          </Paragraph>
          <Paragraph>
            De Persoonsgegevens die we verzamelen zijn afhankelijk van onze interactie met de Gebruiker en kunnen
            informatie bevatten over:
          </Paragraph>
          <Ul>
            <Li>
              <Text type={'text'}>uw verbinding/verificatie op het Platform;</Text>
            </Li>
            <Li>
              <Text type={'text'}>
                identificatiegegevens en uw professionele contactgegevens (zakelijke e-mail, naam, voornaam);
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                uw gebruik van het Platform en het apparaat dat u gebruikt om het Platform te bezoeken, inclusief
                logbestanden (met betrekking tot het aantal bezoeken, bezochte pagina, gebruikte webbrowser), IP-adres,
                tijdzone en cookies (voor meer informatie verwijzen wij u naar de &quot;Cookies&quot; pagina van het
                Platform);
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                schattingen die u via het Platform hebt gemaakt met betrekking tot diagnose, reparatie of recycling
                (&quot;Technische Operaties&quot;) van het voertuig van de Derde Betrokkene, inclusief schattingen die
                niet resulteren in de indiening van een Technische Operatie;
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                een bestelling van Producten bij het Renault R1 distributienetwerk (de verzamelde gegevens zijn met name
                de professionele gegevens van de zakelijke contactpersoon die de levering zal ontvangen);
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                de commerciële relatie (d.w.z. uw eventuele verzoeken om ondersteuning met betrekking tot het
                functioneren van het Platform, klachten en/of uitoefening van uw rechten op persoonsgegevens).
              </Text>
            </Li>
          </Ul>
          <MarginBox mt={15} />
          <Paragraph>
            De gegevens die nodig zijn om te reageren op een verzoek of om te voldoen aan een contract of wettelijke
            verplichting worden vermeld in de verzamelformulieren. Als u de verplichte gegevens niet wilt verstrekken,
            kunnen we uw verzoek mogelijk niet verwerken of u de relevante diensten niet leveren.
          </Paragraph>
          <Paragraph>
            In overeenstemming met onze Algemene Voorwaarden is het Platform niet bedoeld voor minderjarigen en
            verzamelen we daarom niet bewust gegevens over hen. De producten en diensten die wij aanbieden zijn primair
            voor volwassenen. Daarom voeren we geen specifieke verwerking uit op minderjarigen.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'3. WAARVOOR WORDEN PERSOONSGEGEVENS GEBRUIKT?'} />
          <Paragraph>
            We zullen u uitleg geven over de context van het verzamelen van Persoonsgegevens, de doeleinden waarvoor we
            ze verwerken en de duur dat we ze bewaren.
          </Paragraph>
          <MarginBox mt={15} />
          <Section>
            <SectionHeader title={'3.1 CONTEXTE DE LA COLLECTE '} />
            <Paragraph>We verzamelen Persoonsgegevens in het bijzonder wanneer:</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>u het Platform gebruikt;</Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  u rechtstreeks contact hebt met Renault via het Platform (een formulier invullen, contact opnemen);
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>u het Platform bezoekt, dat cookies of andere tracers kan gebruiken;</Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  u een verzoek om technische ondersteuning uitvoert met betrekking tot de werking van het Platform, een
                  Technische Operatie registreert of een technische handeling bewerkt en verstuurt of, indien nodig, een
                  Service Verzoek instelt.
                </Text>
              </Li>
            </Ul>
          </Section>
          <Section>
            <SectionHeader title={'3.2. DOELSTELLINGEN VAN DE VERWERKING VAN PERSOONSGEGEVENS'} />
            <Paragraph>
              Binnen de context van onze relatie en in overeenstemming met de context waarin uw Persoonsgegevens zijn
              verzameld, kunnen wij uw Persoonsgegevens gebruiken om:
            </Paragraph>
            <Ul type={'none'}>
              <Li>
                <Text type={'text'}>A - Onze interacties te beheren;</Text>
              </Li>
              <Li>
                <Text type={'text'}>B - Onze commerciële relatie beheren;</Text>
              </Li>
              <Li>
                <Text type={'text'}>C - Studies en analyses uit te voeren;</Text>
              </Li>
              <Li>
                <Text type={'text'}>D - Onze marketingactiviteiten te beheren; </Text>
              </Li>
            </Ul>
            <MarginBox mt={10} />
            <Section>
              <SectionHeader title={'3.2.A. ONZE INTERACTIES BEHEREN'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: NL_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-a',
                    width: '50%',
                  },
                  { title: NL_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-a', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    objectives: 'Bezoeken aan het Platform meten en het onderhoud en de beveiliging ervan beheren',
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking is gebaseerd op uw toestemming voor het plaatsen/lezen van "niet-essentiële" cookies in uw terminal (zie de "Cookies" pagina van het Platform), of op ons gerechtvaardigd belang (om u een veilig Platform te bieden)`,
                  },
                  {
                    id: '1',
                    objectives: 'Het beheer van uw authenticatie op het Platform',
                    legal:
                      // eslint-disable-next-line max-len
                      'Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die de Gebruiker met ons is aangegaan (beheerst door de Algemene Voorwaarden)',
                  },
                  {
                    id: '2',
                    objectives: 'Om de Gebruiker verbinding te laten maken met het Platform',
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die de Gebruiker met ons is aangegaan (beheerst door de Algemene Voorwaarden)`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={'3.2.B. ONZE COMMERCIËLE RELATIE BEHEREN'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: NL_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-b',
                    width: '50%',
                  },
                  { title: NL_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-b', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    // eslint-disable-next-line max-len
                    objectives: `Om de Gebruiker in staat te stellen Technische Operaties uit te voeren, met name in verband met de reparatie van de voertuigen`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die wij met de Gebruiker zijn aangegaan (beheerst door de Algemene Voorwaarden).`,
                  },
                  {
                    id: '1',
                    // eslint-disable-next-line max-len
                    objectives: `Om een Gebruiker in staat te stellen technische aftersales documentatie betreffende de producten van Renault (zoals: reserveonderdelen,  arbeidstijd, reparatiemethoden en diagnostiek, tutorials, zonder volledig te zijn) en/of merken van andere autofabrikanten in te zien`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die wij met de Gebruiker zijn aangegaan (beheerst door de Algemene Voorwaarden).`,
                  },
                  {
                    id: '2',
                    // eslint-disable-next-line max-len
                    objectives: `Om de Gebruiker in staat te stellen het bedrag verband houdende met de Technische Operaties en, indien nodig, om een Service Verzoek te doen via het Platform`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die wij met de Gebruiker zijn aangegaan (beheerst door de Algemene Voorwaarden).`,
                  },
                  {
                    id: '3',
                    objectives: `Onze commerciële relatie beheren`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die wij met de Gebruikers zijn aangegaan (beheerst door de Algemene Voorwaarden).`,
                  },
                  {
                    id: '4',
                    objectives: `Service Verzoeken met betrekking tot de werking van het Platform te beheren`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking wordt gerechtvaardigd door de uitvoering van de overeenkomst die wij met de Gebruiker zijn aangegaan (beheerst door de Algemene Voorwaarden).`,
                  },
                  {
                    id: '5',
                    // eslint-disable-next-line max-len
                    objectives: `Reageren op eventuele verzoeken om rechten uit te oefenen met betrekking tot uw persoonsgegevens betreffende een (vertegenwoordiger van een) Gebruiker (zie onderdeel 5 hierna over "uw rechten")`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking is gebaseerd op onze wettelijke verplichtingen en kan inhouden dat de identiteit van de verzoeker wordt gecontroleerd`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={'3.2.C. STUDIES EN ANALYSES UITVOEREN'} />
              <Paragraph>
                Er worden onderzoeken en analyses uitgevoerd om onze prestaties te meten, de kwaliteit van onze
                producten en diensten en de mate van klanttevredenheid te beoordelen en deze voortdurend te verbeteren.
              </Paragraph>
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: NL_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-c',
                    width: '50%',
                  },
                  { title: NL_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-c', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    // eslint-disable-next-line max-len
                    objectives: `Beheer van onze tevredenheidsonderzoeken, inclusief het antwoord (d.w.z. uw mening), om de tevredenheid van Gebruikers met betrekking tot het Platform te meten`,
                    legal: `Deze verwerking is gebaseerd op onze gerechtvaardigde belangen (om de prestaties van onze activiteit te meten)`,
                  },
                  {
                    id: '1',
                    objectives: `Onze activiteiten controleren (verslaggeving)`,
                    // eslint-disable-next-line max-len
                    legal: `Deze verwerking is gebaseerd op ons gerechtvaardigd belang (het verbeteren van onze producten en diensten)`,
                  },
                ]}
              />
            </Section>
            <Section>
              <SectionHeader title={'3.2.D. ONZE MARKETINGACTIVITEITEN BEHEREN'} />
              <Table
                pagination={false}
                rowKey={(record) => record.id}
                columns={[
                  {
                    title: NL_OBJECTIVE_TABLE_HEADER,
                    dataIndex: 'objectives',
                    key: 'objectives-3-2-d',
                    width: '50%',
                  },
                  { title: NL_LEGAL_TABLE_HEADER, dataIndex: 'legal', key: 'legal-3-2-d', width: '50%' },
                ]}
                dataSource={[
                  {
                    id: '0',
                    objectives: `Reclamecampagnes sturen naar het Renault-netwerk (B2B) / Gebruikers`,
                    legal: `Deze verwerking is gebaseerd op ons gerechtvaardigd belang (om onze producten bekend te maken)`,
                  },
                ]}
              />
              <MarginBox mt={15} />
              <Paragraph>
                Als we Persoonsgegevens moeten verwerken voor andere doeleinden dan de doeleinden die in de bovenstaande
                tabel worden genoemd, wordt u daarvan vooraf op de hoogte gesteld en nemen we alle aanvullende
                maatregelen die nodig kunnen zijn om ervoor te zorgen dat alle verwerkingen in overeenstemming zijn met
                de wet.
              </Paragraph>
            </Section>
          </Section>
          <Section>
            <SectionHeader title={'3.3. HOE LANG WE PERSOONSGEGEVENS BEWAREN'} />
            <Paragraph>
              In overeenstemming met toepasselijke wet- en regelgeving verplichten wij ons om Persoonsgegevens verwerkt
              in verband met het Platform alleen te bewaren voor de tijd die nodig is om het beoogde doel te bereiken,
              om aan uw behoeften te voldoen of om aan onze wettelijke verplichtingen te voldoen.
            </Paragraph>
            <Paragraph>Bij het bepalen van deze tijd houden we in het bijzonder rekening met het volgende:</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>De tijd die nodig is om een verzoek of klacht te verwerken;</Text>
              </Li>
              <Li>
                <Text type={'text'}>uw interesse in ons Platform;</Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  de noodzaak om een bepaalde geschiedenis bij te houden van interacties met het Platform, deze duur
                  varieert in het bijzonder afhankelijk van het feit of u een Service Verzoek hebt ondertekend of niet;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>Onze wettelijke of reglementaire verplichtingen.</Text>
              </Li>
            </Ul>
            <Paragraph>In het bijzonder:</Paragraph>
            <Ul>
              <Li>
                <Text type={'text'}>
                  Persoonsgegevens die via cookies worden verzameld in het kader van de analyse van de prestaties van de
                  inhoud die u op onze sites raadpleegt en de weergave van gerichte online reclame. Niet-essentiële
                  trackers/cookies, zoals reclamecookies, worden maximaal 13 maanden bewaard vanaf de datum van uw
                  toestemming (mits u uw toestemming niet voor die tijd intrekt). Persoonsgegevens die via cookies
                  worden verzameld, worden 25 maanden verwerkt en vervolgens geanonimiseerd. De bewaartermijn voor uw
                  keuzes (toestemming of weigering van zogenaamde niet-essentiële cookies onderhevig aan voorafgaande
                  verzameling van toestemming) is 6 maanden vanaf het moment van verzamelen van uw toestemming. Kijk
                  voor meer informatie op onze &quot;Cookie&quot; pagina;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  De Persoonsgegevens die worden verwerkt in het kader van het onderhoud en de beveiliging van het
                  Platform en van onze databases worden 6 maanden bewaard (bijv.: logs);
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  De Persoonsgegevens die worden verwerkt in het kader van de geschiedenis van uw Technische Operaties
                  via het Platform en/of een Service Verzoek dat u hebt verzonden, worden 6 maanden bewaard. Schattingen
                  die door de Gebruiker via het Platform zijn gemaakt, worden 3 maanden bewaard.
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  Wanneer de Gebruiker een werknemer is van een professioneel lid van het Renault-netwerk, worden zijn
                  of haar gegevens die via het Platform zijn verzameld bewaard voor de duur van zijn of haar
                  arbeidsovereenkomst met Renault of, indien van toepassing, de toestemming die de werkgever van de
                  Gebruiker heeft gegeven om toegang te krijgen tot het Platform;
                </Text>
              </Li>
              <Li>
                <Text type={'text'}>
                  Persoonsgegevens met betrekking tot verzoeken van een Betrokkene om zijn rechten uit te oefenen,
                  worden maximaal 3 jaar bewaard vanaf de afsluiting van uw verzoek, afhankelijk van het recht dat wordt
                  uitgeoefend. Wanneer het verzamelen van een identiteitsbewijs noodzakelijk was, worden deze gegevens
                  verwijderd zodra de verificatie is voltooid.
                </Text>
              </Li>
            </Ul>
            <Paragraph>
              Wanneer we uw persoonsgegevens niet langer nodig hebben, worden ze verwijderd uit onze systemen of anoniem
              gemaakt zodat we u niet langer kunnen identificeren.
            </Paragraph>
            <Paragraph>
              Het kan echter noodzakelijk zijn om sommige Persoonsgegevens te archiveren om te kunnen reageren op
              eventuele juridische procedures, gedurende de verjaringstermijn die is voorzien in de toepasselijke
              wetgeving.
            </Paragraph>
          </Section>
        </Section>
        <Section>
          <SectionHeader title={'4. WIE HEEFT TOEGANG TOT UW PERSOONSGEGEVENS?'} />
          <Paragraph>
            Om de in deze privacyverklaring genoemde doeleinden te bereiken en alleen voor zover noodzakelijk voor het
            nastreven van deze doeleinden:
          </Paragraph>
          <Ul>
            <Li>
              zorgen wij ervoor dat alleen personen die naar behoren zijn gemachtigd om Persoonsgegevens te verwerken in
              overeenstemming met hun functies en taken, dit mogen doen.
            </Li>
            <Li>
              kunnen we sommige Persoonsgegevens doorgeven aan de volgende ontvangers:
              <Ul type={'circle'}>
                <MarginBox mt={10} />
                <Li>
                  aan de leden van het Renault-netwerk (d.w.z. de leden van het R1-netwerk bij wie de Gebruiker
                  gemachtigd is om een bestelling voor Producten te plaatsen), onder voorbehoud van uw toestemming
                  indien dit wettelijk vereist is.
                </Li>
                <MarginBox mt={10} />
                <Li>
                  Renault en zijn leden van het netwerk hebben namelijk een overeenkomst gesloten waarin zij zich er als
                  onafhankelijke verantwoordelijken voor de verwerking toe verbinden hun verplichtingen uit hoofde van
                  de wet- en regelgeving inzake de bescherming van persoonsgegevens na te komen, met inbegrip van onder
                  meer de volgende beginselen :
                  <Ul>
                    <MarginBox mt={7} />
                    <Li>persoonsgegevens rechtmatig en eerlijk verwerken;</Li>
                    <Li>de nauwkeurigheid en kwaliteit van de gegevens te waarborgen;</Li>
                    <Li>
                      het implementeren van alle passende technische en organisatorische maatregelen om een
                      beveiligingsniveau te garanderen dat in verhouding staat tot het risico en om samen te werken in
                      het geval van een datalek;
                    </Li>
                    <Li>om alleen betrouwbare sub-verwerkers in te huren;</Li>
                    <Li>passende waarborgen te implementeren voor de overdracht van gegevens;</Li>
                    <Li>
                      de rechten van betrokkenen respecteren, waaronder het recht om geïnformeerd te worden over
                      gegevensverwerking.
                    </Li>
                  </Ul>
                  <MarginBox mt={10} />
                  Met betrekking tot het laatste principe worden werknemers van Gebruikers uit het Renault-netwerk
                  geïnformeerd dat deze privacyverklaring een aanvulling is op de informatie over gegevensverwerking die
                  u mogelijk is verstrekt door uw werkgever als professioneel onderdeel van het Renault-netwerk.
                </Li>
              </Ul>
            </Li>
            <MarginBox mt={10} />
            <Li>
              verwerkers van persoonsgegevens in de zin van de toepasselijke regelgeving inzake persoonsgegevens, om de
              hierna opgesomde taken uit te voeren. Wij dragen zorg voor het waarborgen van deze relaties (contracten,
              audits, garanties en veiligheidstests...) en geven hen instructies met betrekking tot de taken waarvan wij
              hen met name belasten:
              <Ul type={'circle'}>
                <MarginBox mt={7} />
                <Li>Hosting,</Li>
                <Li>
                  Beveiliging en onderhoud van het Platform en de via het Platform verzamelde en/of verwerkte databases,
                </Li>
                <Li>Commerciële prospectie bij professionele leden van het Renault-netwerk (B2B),</Li>
                <Li>
                  Beheer van klantrelaties en technische ondersteuning met betrekking tot de werking van het Platform,
                </Li>
                <Li>Publieksmetingen op het platform,</Li>
                <Li>Meting van de tevredenheid van de gebruiker over het platform.</Li>
                <MarginBox mt={7} />
              </Ul>
            </Li>
          </Ul>
          <Paragraph>
            We kunnen verplicht worden om Persoonsgegevens aan derden te verstrekken om te voldoen aan een wettelijke
            verplichting of administratieve of gerechtelijke beslissing.
          </Paragraph>
          <Paragraph>
            Renault kan Persoonsgegevens delen met derden bij het overdragen van verplichtingen en rechten met
            betrekking tot de contractuele relatie tussen u en Renault aan dergelijke derden in het geval van de
            overdracht van een bedrijfsonderdeel, een fusie door oprichting van een nieuw bedrijf, een splitsing of een
            wijziging van de zeggenschap die van invloed is op Renault. Voorafgaand aan een dergelijke gebeurtenis zal
            Renault u apart informeren over de informatie met betrekking tot het delen van uw gegevens en uw toestemming
            vragen indien dit wettelijk vereist is.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'5. WAT ZIJN UW RECHTEN ALS BETROKKENE?'} />
          <Section>
            <SectionHeader title={'5.1. UW RECHTEN'} />
            <Paragraph>
              U hebt als Betrokkene verschillende rechten onder de regelgeving voor de bescherming van Persoonsgegevens:
            </Paragraph>
            <Paragraph>
              Het recht om bezwaar te maken tegen de verwerking van uw Persoonsgegevens, mits u redenen hebt die verband
              houden met uw specifieke situatie, en het recht om te verzoeken om beperking van de verwerking van uw
              persoonsgegevens, in sommige gevallen zoals bepaald in de regelgeving.
            </Paragraph>
            <Paragraph>
              Het recht om bezwaar te maken tegen communicatie van ons: u kunt te allen tijde verzoeken om niet langer
              communicatie van ons te ontvangen.
            </Paragraph>
            <Paragraph>
              Het recht om uw toestemming te allen tijde in te trekken voor de doeleinden waarvoor wij uw toestemming
              hebben verkregen (zie hierboven 3.2. De doelstelling van de verwerking van uw Persoonsgegevens).
            </Paragraph>
            <Paragraph>
              Recht op informatie: u hebt recht op duidelijke, transparante en begrijpelijke informatie over hoe we uw
              Persoonsgegevens gebruiken en over uw rechten. Deze privacyverklaring is daar een voorbeeld van.
            </Paragraph>
            <Paragraph>
              Recht op toegang tot uw Persoonsgegevens: u hebt het recht om informatie te verkrijgen over de verwerking
              van uw Persoonsgegevens (met inbegrip van de gegevens die worden gebruikt, voor welke doeleinden, enz).
            </Paragraph>
            <Paragraph>
              Recht op rectificatie: u hebt het recht om uw Persoonsgegevens te corrigeren als deze onnauwkeurig of
              onvolledig zijn, ondanks onze inspanningen om ze actueel te houden, zodat wij kunnen voldoen aan onze
              verplichting om actuele gegevens over u te hebben.
            </Paragraph>
            <Paragraph>
              Een recht op overdraagbaarheid van uw gegevens, d.w.z., onder bepaalde voorwaarden, het recht om de
              Persoonsgegevens die u aan ons hebt verstrekt, te ontvangen in een gestructureerd, algemeen gebruikt
              computerformaat, en om deze door te geven aan een derde partij indien dit technisch mogelijk is.
            </Paragraph>
            <Paragraph>
              Recht op wissen (of recht om vergeten te worden): u hebt het recht om uw Persoonsgegevens te laten wissen
              of verwijderen. Dit recht kan worden beperkt in het licht van onze contractuele of wettelijke
              verplichtingen (lopend contract) (met name het voorkomen van gerechtelijke stappen).
            </Paragraph>
            <Paragraph>
              Tot slot hebt u het recht om een klacht in te dienen bij een toezichthoudende autoriteit voor de
              bescherming van persoonsgegevens in Nederland, de Autoriteit Persoonsgegevens. We raden u aan om contact
              met ons op te nemen voordat u een klacht indient, zodat we samen kunnen proberen uw probleem op te lossen.
            </Paragraph>
          </Section>
          <Section>
            <SectionHeader title={'5.2. HOE OEFENT U ALS BETROKKENE UW RECHTEN UIT?'} />
            <Paragraph>
              Om uw rechten uit te oefenen, kunt u uw verzoek op elk gewenst moment per post naar het volgende adres
              sturen
            </Paragraph>
            <Paragraph>
              Renault België N.V., Juridische afdeling - Functionaris Gegevensbescherming, Bergensesteenweg 281, 1620 te
              Drogenbos, België.
            </Paragraph>
            <Paragraph>of</Paragraph>
            <Paragraph>
              een e-mail naar het volgende adres:{' '}
              <Link to={'mailto:dataprotection-com@renault.com'} external newTab>
                dataprotection-com@renault.com
              </Link>
              .
            </Paragraph>
            <Paragraph>
              In beide gevallen dient u, om de verwerking van uw verzoek te vergemakkelijken, aan te geven dat uw
              verzoek betrekking heeft op het Platform &quot;RpartStore&quot;.
            </Paragraph>
            <Paragraph>
              Als wij niet zeker zijn van uw identiteit, kan u worden gevraagd om uw identiteit te bewijzen (d.w.z. de
              kopie van uw identiteit wordt uitsluitend verwerkt om te reageren op uw verzoek en wordt onmiddellijk na
              verificatie verwijderd).
            </Paragraph>
            <Paragraph>
              We zullen al het mogelijke doen om zo snel mogelijk op uw verzoek te reageren, maar niet later dan één
              maand na ontvangst van uw verzoek. In overeenstemming met de toepasselijke regelgeving behouden we ons het
              recht voor om deze periode te verlengen tot drie maanden als uw verzoek bijzonder complex is.
            </Paragraph>
          </Section>
        </Section>
        <Section>
          <SectionHeader title={'6. HOE WORDEN UW PERSOONSGEGEVENS BEVEILIGD?'} />
          <Paragraph>
            Uw persoonsgegevens worden opgeslagen op beveiligde servers. We treffen passende maatregelen voor
            gegevensbeveiliging en -bescherming in overeenstemming met de nieuwste technologieën en eisen dit van onze
            verwerkers en partners.
          </Paragraph>
          <Paragraph>
            Wanneer de verwerking van Persoonsgegevens gepaard gaat met de overdracht van gegevens, zorgen we ervoor dat
            deze overdracht plaatsvindt onder passende voorwaarden die een adequaat niveau van bescherming, beveiliging
            en vertrouwelijkheid garanderen.
          </Paragraph>
          <Paragraph>
            Waar mogelijk worden Persoonsgegevens verwerkt in de Europese Economische Ruimte (EER). Aangezien sommige
            van onze dienstverleners of hun verwerkers echter gevestigd zijn in landen buiten de EER, kunnen uw
            persoonsgegevens in die landen worden verwerkt. In sommige van deze landen gelden mogelijk andere regels
            voor Persoonsgegevens dan in de Europese Unie. In een dergelijk geval (i) besteden we bijzondere aandacht om
            ervoor te zorgen dat deze overdracht wordt uitgevoerd in overeenstemming met de toepasselijke regelgeving en
            (ii) treffen we waarborgen die een adequaat niveau van bescherming van uw privacy en fundamentele rechten
            waarborgen (in het bijzonder door gebruik te maken van de modelcontractbepalingen van de Europese
            Commissie). Op eenvoudig verzoek, gericht aan het adres vermeld onder 5. &quot;Wat zijn uw rechten?&quot;,
            kunnen wij u meer informatie verstrekken over dergelijke overdrachten (in het bijzonder de
            modelcontractbepalingen van de Europese Commissie).
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'7. OVERIGE INFORMATIE DIE U NODIG KUNT HEBBEN'} />
          <Paragraph>
            Wij kunnen ook op geautomatiseerde wijze technische informatie over u verzamelen wanneer u een van onze
            sites bezoekt of een van onze mobiele applicaties gebruikt, met name uw IP-adres, uw navigatieparameters,
            door middel van tracers (cookies).
          </Paragraph>
          <Paragraph>
            Voor meer informatie nodigen we u uit om de «{' '}
            <Link to={ROUTER_COOKIES(Country.BE.key)} newTab>
              Cookies
            </Link>{' '}
            » pagina van het Platform te raadplegen.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'8. WIJZIGING VAN DEZE PRIVACYVERKLARING'} />
          <Paragraph>
            We kunnen deze privacyverklaring van tijd tot tijd wijzigen. Wij zullen u informeren en/of uw toestemming
            vragen wanneer dat nodig of vereist is. We raden u daarom aan deze privacyverklaring te raadplegen telkens
            wanneer u ons Platform bezoekt om de meest recente versie te bekijken.
          </Paragraph>
        </Section>
        <Flex justify={'flex-end'}>
          <Text type={'text'}>Bijgewerkt op 23 oktober 2023</Text>
        </Flex>
        <MarginBox mt={200} />
      </Flex>
    </Container>
  );
};
